<template>
  <el-overlay v-if="isNotAvailable" :mask="true" class="cart-not-available">
    <div class="overlay-content">
      <a v-if="cart.status === 'delete'" href="javascript:void(0)" class="text-white font-weight-medium line-clamp-1"
        @click="swalDetail($t('carts.modelInvalid'), 'error', '移除商品', () => { handleOnClickCartRemove() })">
        {{ $t('carts.modelInvalid') }}
      </a>
      <a v-else-if="cart.status === 'event_delete' || cart.status === 'event_lower'" href="javascript:void(0)"
        class="text-white font-weight-medium line-clamp-1"
        @click="swalDetail(promotionEventExceptionMessage, 'error', '移除商品', () => { handleOnClickCartRemove() })">
        {{ promotionEventExceptionMessage }}
      </a>
      <a v-else-if="cart.status !== 'shelves'" href="javascript:void(0)"
        class="text-white font-weight-medium line-clamp-1"
        @click="swalDetail($t('carts.ProductOff'), 'error', '移除商品', () => { handleOnClickCartRemove() })">
        {{ $t('carts.ProductOff') }}
      </a>
      <a v-else-if="cart.quantity > cart.max_order_quantity" href="javascript:void(0)"
        class="text-white font-weight-medium line-clamp-1"
        @click="swalDetail(limitedMaxOrderQtyMessage, 'error', '修改訂購數量', () => { handleOnClickMaxQty() })">
        {{ limitedMaxOrderQtyMessage }}
      </a>
      <template v-else-if="!cart.is_valid">
        <a v-if="!cart.available_payment_types.includes(form.payment_type)" href="javascript:void(0)"
          class="text-white font-weight-medium line-clamp-1"
          @click="swalDetail(limitedPaymentTypesMessage, 'error', '查看付款方式', () => { handleOnClickScrollTo('payment-sector') })">
          {{ limitedPaymentTypesMessage }}
        </a>
        <a v-else-if="!cart.available_delivery_types.includes(form.delivery_type)" href="javascript:void(0)"
          class="text-white font-weight-medium line-clamp-1"
          @click="swalDetail(limitedDeliveryTypesMessage, 'error', '查看配送方式', () => { handleOnClickScrollTo('logistic-sector') })">
          {{ limitedDeliveryTypesMessage }}
        </a>
        <a v-else-if="state.isOuterIsland[form.delivery_type] && cart.is_excluded_outer_island" href="javascript:void(0)"
          class="text-white font-weight-medium line-clamp-1"
          @click="swalDetail(excludeOuterIslandMessage, 'error', '查看配送方式', () => { handleOnClickScrollTo('logistic-sector') })">
          {{ excludeOuterIslandMessage }}
        </a>
      </template>
      <a v-else href="javascript:void(0)" class="text-white font-weight-medium line-clamp-1"
        @click="swalDetail(unknownExceptionMessage, 'error', '移除商品', () => { handleOnClickCartRemove() })">
        {{ unknownExceptionMessage }}
      </a>
    </div>
  </el-overlay>
</template>

<script setup>
import { computed, toRefs } from 'vue';
import { keyBy } from 'lodash';
import { ElOverlay } from 'element-plus';

const props = defineProps({
  cart: Object,
  state: Object,
  data: Object,
  form: Object,
  isCartEditing: { type: Boolean, default: false },
});

const { cart, isCartEditing, data, form, state } = toRefs(props);

const isNotAvailable = computed(() => {
  if (cart.value.status === 'delete') {
    return true;
  }
  if (cart.value.status === 'event_delete' || cart.value.status === 'event_lower') {
    return true;
  }
  if (cart.value.status !== 'shelves') {
    return true;
  }
  if (cart.value.quantity > cart.value.max_order_quantity) {
    return true;
  }
  if (!isCartEditing.value && !cart.value.is_valid) {
    return true;
  }
  return false;
});

const paymentMap = computed(() => keyBy(data.value.payments || [], 'type'));
const logisticMap = computed(() => keyBy(data.value.logistics || [], 'delivery_type'));

const limitedPaymentTypesMessage = computed(() => `僅限於【${cart.value.available_payment_types.map((i) => paymentMap.value?.[i]?.name || i).join("/")}】`);
const limitedDeliveryTypesMessage = computed(() => `僅限於【${cart.value.available_delivery_types.map((i) => logisticMap.value?.[i]?.name || i).join("/")}】`);
const limitedMaxOrderQtyMessage = computed(() => `已超過最大訂購上限 - ${cart.value.max_order_quantity}`);
const excludeOuterIslandMessage = computed(() => `不適用【外島配送】`);
const promotionEventExceptionMessage = computed(() => `促銷活動已下架或尚未開始`);
const unknownExceptionMessage = computed(() => `已下架或庫存不足`);

const emit = defineEmits(['update']);

const handleOnClickCartRemove = () => {
  emit('update', 'remove');
};

const handleOnClickMaxQty = () => {
  emit('update', 'max-qty');
};

const handleOnClickScrollTo = (scrollToElemCalssName) => {
  setTimeout(() => {
    // 檢查有沒有被 swal2 遮到，等 swal2 完全關閉再 scrollIntoView
    const swalContainer = document.getElementsByClassName('swal2-container')?.length;
    if (swalContainer) {
      setTimeout(() => handleOnClickScrollTo(scrollToElemCalssName), 100);
      return;
    }
    // 確認 swal2 已關閉，執行 scrollIntoView
    const scrollElem = document.getElementsByClassName(
      `${scrollToElemCalssName}`
    )?.[0];
    scrollElem &&
      scrollElem.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
  }, 100);
};

const swalDetail = (text, type = 'error', confirmButtonText = null, confirmButtonAction = null) => {
  swal.fire({
    type,
    text,
    confirmButtonText,
    confirmButtonColor: 'var(--primary_color)',
  }).then((result) => {
    if (result.value && confirmButtonAction) {
      confirmButtonAction()
    }
  })
}

</script>

<style lang="scss">
.cart-not-available {
  position: absolute !important;
  top: 0 !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  .overlay-content {
    width: 100%;
    height: 100%;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    div {
      font-size: 1.3rem;
      text-shadow: #000 0.1em 0.1em 1em;
    }
  }
}
</style>